/**
 * UserContext — single source of truth for auth, user, and persona state.
 * 
 * Internally delegates to extracted hooks:
 * - useAuthState: session management, social swap, auth bootstrap
 * - useProfileOperations: profile CRUD, CNIC, onboarding, resources
 * 
 * All types re-exported for backward compatibility.
 */
import React, { createContext, useContext, useCallback, useRef, ReactNode } from 'react';
import { isBrowser, safeCallback } from '@/utils/environmentSafety';
import { supabase } from '@/integrations/supabase/client';
import { authService, mapDbPersonaToFrontend } from '@/services/supabase';
import { syncBiometricSessionToken, hasBiometricEnrollment } from '@/services/biometricService';
import { isInternalEmail } from '@/utils/emailDisplay';
import { isNativeApp } from '@/lib/nativeApp';
import { tapFeedback } from '@/lib/haptics';
import { applyPersonaStatusBar } from '@/lib/nativeStatusBar';
import { isOAuthSignupPending } from '@/utils/oauthIntent';
import { identifyAnalyticsUser } from '@/utils/analyticsIdentity';
import { captureError } from '@/utils/captureError';
import type { User as SupabaseUser } from '@supabase/supabase-js';

// Re-export all types for backward compatibility
export type {
  UserPersona, TransactionType, Transaction, Resource,
  JobApplication, TrialDetails, CNICFiles, User, UserProfile
} from './user/types';

import type {
  UserPersona, User, Resource, JobApplication, CNICFiles
} from './user/types';

import { useAuthState } from './user/useAuthState';
import { useProfileOperations } from './user/useProfileOperations';
import { safeRandomUUID } from "@/utils/safeUuid";

interface UserContextType {
  user: User | null;
  persona: UserPersona | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNewUser: boolean;
  login: (user: User, persona: UserPersona) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  setPersona: (persona: UserPersona) => void;
  register: (userData: { name: string; cnic: string; password: string }) => Promise<boolean>;
  addResource: (resource: Omit<Resource, 'id'>) => void;
  updateResource: (id: string, updates: Partial<Resource>) => void;
  removeResource: (id: string) => void;
  addJobApplication: (application: Omit<JobApplication, 'id'>) => void;
  updateCNICVerification: (verified: boolean, files?: CNICFiles) => void;
  uploadProfileImage: (file: File) => Promise<boolean>;
  uploadCNICFiles: (frontFile: File, backFile: File) => Promise<boolean>;
  getCNICFiles: () => { frontPreview?: string; backPreview?: string } | null;
  completeOnboarding: () => void;
  calculateProfileCompletion: () => { userCompleted: number; adminPending: number; total: number };
  refreshSession: () => Promise<void>;
}

const defaultContextValue: UserContextType = {
  user: null,
  persona: null,
  isAuthenticated: false,
  isLoading: true,
  isNewUser: false,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
  setPersona: () => {},
  register: async () => false,
  addResource: () => {},
  updateResource: () => {},
  removeResource: () => {},
  addJobApplication: () => {},
  updateCNICVerification: () => {},
  uploadProfileImage: async () => false,
  uploadCNICFiles: async () => false,
  getCNICFiles: () => null,
  completeOnboarding: () => {},
  calculateProfileCompletion: () => ({ userCompleted: 0, adminPending: 0, total: 0 }),
  refreshSession: async () => {}
};

const UserContext = createContext<UserContextType>(defaultContextValue);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const freshSignupPersonaRef = useRef<UserPersona | null>(null);

  // Profile loader called by auth state hook when session is established
  const loadUserProfile = useCallback(async (supabaseUser: SupabaseUser) => {
    try {
      const { profile, error } = await authService.getProfile(supabaseUser.id);

      if (error || !profile) {
        // Validate the JWT actually maps to an existing auth user. If not,
        // the session is stale (user deleted) — clear it so the app stops
        // spinning on "Loading profile..." and routes back to /login.
        try {
          const { data: { user: authProbe }, error: probeErr } = await supabase.auth.getUser();
          const probeMsg = probeErr?.message || '';
          const userGone = !authProbe ||
            /user_not_found|user from sub claim in jwt does not exist|missing sub claim|bad_jwt/i.test(probeMsg);
          if (userGone) {
            try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* no-op */ }
            try {
              Object.keys(window.localStorage)
                .filter((k) => k.startsWith('sb-') && k.includes('-auth-token'))
                .forEach((k) => window.localStorage.removeItem(k));
            } catch { /* no-op */ }
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
              window.location.replace('/login');
            }
            return;
          }
        } catch { /* no-op */ }

        // Auth user is real but no profile row yet — likely an in-flight OAuth signup
        // whose persona-selection step has not run apply_oauth_persona yet. Bounce
        // to persona-selection so the user can finish setup instead of infinite spinning.
        if (typeof window !== 'undefined' &&
            !/^\/(persona-selection|auth\/callback|login)/.test(window.location.pathname)) {
          window.location.replace('/persona-selection?source=oauth');
        }
        if (import.meta.env.DEV) console.error('Failed to load profile:', error);
        return;
      }


      const mappedUser: User = {
        id: profile.id,
        name: profile.full_name,
        email: profile.email || (supabaseUser.email && !isInternalEmail(supabaseUser.email) ? supabaseUser.email : ''),
        phone: profile.phone || undefined,
        cnic: profile.cnic || undefined,
        profileImage: profile.avatar_url || undefined,
        profileCompleted: (profile.profile_completion_percentage || 0) >= 80,
        emailVerified: profile.email_verified || false,
        cnicVerified: profile.cnic_verified === 'verified',
        cnicUploaded: false,
        onboardingCompleted: profile.onboarding_completed || false,
        backgroundVerified: profile.background_verified || false,
        location: profile.city || undefined,
        joinDate: profile.created_at,
      };

      // Show linking success toast
      if (isBrowser) {
        const linkSuccess = sessionStorage.getItem('behtari_link_success');
        if (linkSuccess) {
          sessionStorage.removeItem('behtari_link_success');
          const [provider, email] = linkSuccess.split(':');
          const providerName = provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : provider;
          const { toast } = await import('sonner');
          toast.success(`${providerName} account connected successfully${email ? ` (${email})` : ''}`);
        }
      }

      // Resolve persona — freshSignupPersonaRef takes priority to prevent race condition
      const validPersonas = ['job-seeker', 'individual-employer', 'business-employer', 'provider', 'agency', 'admin'];
      const storedPersona = isBrowser ? localStorage.getItem('behtari_persona') : null;
      const dbPersona = mapDbPersonaToFrontend(profile.persona);

      // OAuth signup race guard: if an OAuth SIGNUP with a chosen persona is
      // still in-flight, the DB persona may still be the trigger default
      // ('job_seeker'). Do NOT hydrate persona from the DB yet — finalizeOAuth
      // will run apply_oauth_persona and re-trigger this load afterwards.
      const oauthSignupPending = isBrowser && isOAuthSignupPending();

      let finalPersona: string | null;
      if (freshSignupPersonaRef.current) {
        finalPersona = freshSignupPersonaRef.current;
        freshSignupPersonaRef.current = null;
      } else if (oauthSignupPending) {
        // Skip hydration — keep current persona (may be null) until the
        // finalizer clears the intent and a subsequent load runs.
        finalPersona = null;
      } else if (storedPersona === 'admin') {
        // ⚠️ DO NOT REMOVE THIS BRANCH. Admin persona resolution has regressed
        // twice (July 2026) when refactors simplified this if/else chain: without
        // it, an admin hydrates as 'job-seeker' until the async role enrichment
        // resolves, breaking every persona==='admin' gate (UniversalMessagesPanel
        // Support tab, WhatsAppStyleChatBubble suppression, admin status bar).
        // profiles.persona has no 'admin' enum value, so dbPersona can never be
        // 'admin' — a stored 'admin' can only have been written after a prior
        // successful user_roles check. Trust it for instant admin hydration;
        // the role enrichment below re-verifies and downgrades if revoked.
        finalPersona = 'admin';
      } else if (dbPersona && validPersonas.includes(dbPersona)) {
        finalPersona = dbPersona;
      } else if (storedPersona && validPersonas.includes(storedPersona)) {
        finalPersona = storedPersona;
      } else {
        finalPersona = 'job-seeker';
      }

      if (finalPersona) {
        authState.setUser(mappedUser);
        authState.setPersona(finalPersona as UserPersona);
        void applyPersonaStatusBar(finalPersona);
        if (isBrowser && validPersonas.includes(finalPersona)) {
          localStorage.setItem('behtari_persona', finalPersona);
        }
      }

      // Analytics identity. THIS is the universal call site: loadUserProfile
      // runs for every authenticated session — password, biometric, OAuth and
      // app resume — whereas Login.tsx only ever covered the password form.
      // Biometric and OAuth sign-ins were therefore anonymous in replays, which
      // is exactly the population the OAuth-merge investigation needed to watch.
      // Name + persona only; never CNIC/phone/email. See identifyAnalyticsUser.
      identifyAnalyticsUser(profile.id, {
        persona: finalPersona,
        fullName: profile.full_name,
      });

      // Non-critical profile enrichments run after the shell can mount. A slow
      // wallet/document/role request must never block dashboard hydration.
      Promise.allSettled([
        supabase
          .from('user_documents')
          .select('document_type')
          .eq('user_id', supabaseUser.id)
          .in('document_type', ['cnic_front', 'cnic_back']),
        authService.getWallet(supabaseUser.id),
        authService.getUserRoles(supabaseUser.id),
      ]).then(([docsResult, walletResult, rolesResult]) => {
        const updates: Partial<User> = {};
        let isAdminUser = false;

        if (docsResult.status === 'fulfilled') {
          const cnicDocs = docsResult.value.data;
          updates.cnicUploaded = (cnicDocs?.some(d => d.document_type === 'cnic_front') || false)
            && (cnicDocs?.some(d => d.document_type === 'cnic_back') || false);
        }

        if (walletResult.status === 'fulfilled' && walletResult.value.wallet) {
          const wallet = walletResult.value.wallet;
          updates.wallet = {
            balance: wallet.balance,
            pendingAmount: wallet.pending_balance,
            currency: wallet.currency || 'PKR',
            totalEarnings: wallet.total_earned,
          };
        }

        // ⚠️ `rolesCheckSucceeded` is load-bearing — see the downgrade guard below.
        // getUserRoles() NEVER rejects: its own try/catch converts every failure
        // (RLS denial, network blip, 5xx, offline) into `{ roles: [], error: msg }`.
        // So `status === 'fulfilled'` is ALWAYS true and, on its own, says nothing
        // about whether the check actually ran. The `error` field is the only
        // signal that separates "verified: not an admin" from "could not tell".
        const rolesCheckSucceeded =
          rolesResult.status === 'fulfilled' && rolesResult.value.error === null;

        if (rolesResult.status === 'fulfilled') {
          const roles = rolesResult.value.roles;
          isAdminUser = roles.includes('admin') || roles.includes('super_admin');
          if (isAdminUser) {
            updates.role = 'admin';
          }
        }

        if (Object.keys(updates).length > 0) {
          authState.setUser(current => current?.id === mappedUser.id ? { ...current, ...updates } : current);
        }

        // ⚠️ DO NOT REMOVE — second half of the twice-regressed admin persona
        // resolution (see the storedPersona === 'admin' branch above).
        // Admin persona resolution: profiles.persona has no 'admin' enum value,
        // so the synchronous resolution above can never yield 'admin' from the
        // DB. Promote/demote here based on the verified user_roles result.
        // Persona-level admin gates (e.g. UniversalMessagesPanel's Support tab)
        // depend on persona === 'admin', not just user.role. This stays inside
        // the non-blocking enrichment so dashboard hydration is never delayed.
        if (rolesResult.status === 'fulfilled') {
          if (isAdminUser && finalPersona !== 'admin') {
            authState.setPersona('admin' as UserPersona);
            void applyPersonaStatusBar('admin');
            if (isBrowser) {
              try { localStorage.setItem('behtari_persona', 'admin'); } catch { /* no-op */ }
            }
          } else if (!isAdminUser && finalPersona === 'admin' && rolesCheckSucceeded) {
            // ⚠️ THE `rolesCheckSucceeded` CONDITION IS A BUG FIX — DO NOT DROP IT
            // (2026-08-28, third incident in this block; the two above are July 2026).
            //
            // Reported: the admin's chat bubble showed the correct slate colour and
            // then flipped to job-seeker blue seconds after login, and the Messages
            // Support tab fell back to the consumer menu. Both gate on
            // persona === 'admin', and this branch was what took it away.
            //
            // The RLS hypothesis was tested against the LIVE database and is FALSE.
            // Impersonating the real admin as `authenticated`, the exact client
            // query returns 3 rows {user, admin, super_admin}; a stranger control
            // returns 0, proving RLS was genuinely enforced during the test. The
            // policy "Users can view their own roles" (auth.uid() = user_id) is
            // correct and non-recursive. The data source was never the problem, so
            // it is deliberately NOT swapped to the is_admin RPC — that would make
            // this WORSE, because authService.isAdmin() returns a bare `false` on
            // error with no error channel at all, permanently collapsing the
            // distinction this fix depends on.
            //
            // The real cause: getUserRoles() swallows EVERY failure into
            // `{ roles: [], error: msg }`, so a transient network/auth blip is
            // indistinguishable from "this user is not an admin" — and this branch
            // then wrote 'job-seeker' into localStorage, poisoning the fast path so
            // the demotion survived reloads until a later check happened to succeed.
            // Downgrade now requires a check that DEFINITIVELY succeeded. A genuine
            // revocation (successful query, no admin role) still downgrades exactly
            // as before; server-side RLS remains the real boundary either way.
            const fallback = (dbPersona && validPersonas.includes(dbPersona)) ? dbPersona : 'job-seeker';
            authState.setPersona(fallback as UserPersona);
            void applyPersonaStatusBar(fallback);
            if (isBrowser) {
              try { localStorage.setItem('behtari_persona', fallback); } catch { /* no-op */ }
            }
          } else if (!isAdminUser && finalPersona === 'admin' && !rolesCheckSucceeded) {
            // Instrument the guard clause, not just the happy path (CLAUDE.md §29 D:
            // "a capture site placed after the thing that fails is not
            // instrumentation"). This path had ZERO telemetry, which is why an admin
            // silently losing their persona was only ever caught by eye. Keeping the
            // admin persona here is the deliberate choice; this records that we could
            // not verify, so a real revocation that never verifies stays visible.
            captureError('admin-role-check-inconclusive', new Error('user_roles check failed; keeping admin persona'), {
              reason: rolesResult.status === 'fulfilled' ? rolesResult.value.error : 'promise-rejected',
            });
          }
        }
      }).catch((error) => {
        if (import.meta.env.DEV) console.warn('Non-critical profile enrichment failed:', error);
      });

      // Self-heal: ensure a worker_profiles row exists for worker-side personas.
      // This makes their skills/portfolio surfaces queryable even before they fill
      // anything in. Fire-and-forget — failure is non-blocking.
      if (finalPersona === 'job-seeker' || finalPersona === 'provider') {
        supabase
          .from('worker_profiles')
          .upsert({ user_id: profile.id }, { onConflict: 'user_id', ignoreDuplicates: true })
          .then(({ error }) => {
            if (error && import.meta.env.DEV) {
              console.warn('worker_profiles self-heal failed:', error.message);
            }
          });
      }

      // Update login timestamp for greeting logic
      if (isBrowser) {
        const lastLogin = localStorage.getItem('behtari_last_login_at');
        authState.setIsNewUser(!lastLogin);
        localStorage.setItem('behtari_last_login_at', Date.now().toString());
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error loading user profile:', error);
    }
  }, []);

  const authState = useAuthState(loadUserProfile);
  const profileOps = useProfileOperations(authState.user, authState.setUser, authState.session);

  const refreshSession = async () => {
    const { data: { session: newSession } } = await supabase.auth.getSession();
    authState.setSession(newSession);
    if (newSession?.user) {
      await loadUserProfile(newSession.user);
    }
  };

  const login = safeCallback((userData: User, userPersona: UserPersona) => {
    authState.setUser(userData);
    authState.setPersona(userPersona);
    if (isBrowser) {
      try { localStorage.setItem('behtari_persona', userPersona); } catch { /* no-op */ }
    }
  });

  const register = async (userData: { name: string; cnic: string; password: string }): Promise<boolean> => {
    try {
      const email = `${safeRandomUUID()}@auth.behtari.internal`;
      
      const { user: newUser, error } = await authService.signUp(email, userData.password, {
        full_name: userData.name,
        persona: 'job_seeker',
        cnic: userData.cnic,
      });
      
      if (error || !newUser) {
        if (import.meta.env.DEV) console.error('Registration failed:', error);
        return false;
      }
      
      authState.setIsNewUser(true);
      if (isBrowser) localStorage.setItem('behtari_is_new_user', 'true');
      
      return true;
    } catch (error) {
      if (import.meta.env.DEV) console.error('Registration failed:', error);
      return false;
    }
  };

  const logout = safeCallback(async () => {
    void tapFeedback(); // native haptic on logout; no-op on web

    let preserveBio = true;
    try { preserveBio = hasBiometricEnrollment(); } catch { preserveBio = true; }

    if (preserveBio) {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user && currentSession.refresh_token) {
          syncBiometricSessionToken(currentSession.user.id, currentSession.refresh_token);
        }
      } catch { /* no-op */ }
    }

    await authService.signOut(preserveBio);
    authState.setUser(null);
    authState.setPersona(null);
    authState.setSession(null);
    authState.setIsNewUser(false);

    // Native: ensure no ghost session survives in the WebView after logout.
    // When biometric is enrolled (preserveBio) we MUST NOT wipe storage — the
    // biometric refresh token was just synced above and re-auth depends on it,
    // so we only clear sessionStorage. With no biometric enrollment a full
    // clear is safe. Guarded by isNativeApp() → completely inert on web.
    if (isNativeApp()) {
      try {
        if (preserveBio) {
          sessionStorage.clear();
        } else {
          localStorage.clear();
          sessionStorage.clear();
        }
      } catch { /* no-op */ }
    }

    if (isBrowser) {
      try {
        localStorage.removeItem('behtari_persona');
        // Ownership stamp for the cached persona — must go with it, or the next
        // user on this device inherits a stale "this persona is yours" claim.
        localStorage.removeItem('behtari_persona_uid');
        // Preference CACHES. Each of these is now account-backed in
        // `user_preferences`, so clearing them here loses nothing — the next
        // sign-in re-hydrates from the DB. They must still be cleared, for the
        // same cross-user-bleed reason the theme key is: without it, the next
        // user on a shared device briefly renders under the previous user's
        // text size / simple mode / theme before hydration lands.
        localStorage.removeItem('behtari_font_size');
        localStorage.removeItem('behtari_simple_mode');
        localStorage.removeItem('behtari-theme');
        // `behtari_language` is deliberately NOT cleared. It is account-backed
        // too (user_preferences.ui_language, hydrated by
        // LanguageContext.refreshAccountLanguage), but unlike the keys above it
        // also governs the SIGNED-OUT experience. Clearing it would drop a
        // low-literacy Urdu user back into an English landing page the moment
        // they log out, and language carries none of the cross-user bleed risk
        // that motivates the clears above — it is visible and one tap to change.
        window.location.replace('/');
      } catch { /* no-op */ }
    }
  });

  const setPersona = (newPersona: UserPersona) => {
    freshSignupPersonaRef.current = newPersona;
    authState.setPersona(newPersona);
    void applyPersonaStatusBar(newPersona);
    if (isBrowser) {
      try { localStorage.setItem('behtari_persona', newPersona); } catch { /* no-op */ }
    }
  };

  return (
    <UserContext.Provider value={{
      user: authState.user,
      persona: authState.persona,
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.isLoading,
      isNewUser: authState.isNewUser,
      login,
      logout,
      updateUser: profileOps.updateUser,
      setPersona,
      register,
      addResource: profileOps.addResource,
      updateResource: profileOps.updateResource,
      removeResource: profileOps.removeResource,
      addJobApplication: profileOps.addJobApplication,
      updateCNICVerification: profileOps.updateCNICVerification,
      uploadProfileImage: profileOps.uploadProfileImage,
      uploadCNICFiles: profileOps.uploadCNICFiles,
      getCNICFiles: profileOps.getCNICFiles,
      completeOnboarding: profileOps.completeOnboarding,
      calculateProfileCompletion: profileOps.calculateProfileCompletion,
      refreshSession
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  return context;
};
